import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import "react-datepicker/dist/react-datepicker.css";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { getContractAsync } from "src/api/contract/contract-api";
import type { ContractModel } from "src/api/contract/contract-types";
import { useApiOperation } from "src/api/hooks";
import { usePartnerNavigate } from "src/api/hooks/usePartnerNavigate";
import { getContractAccessLogsByPageTokenAsync } from "src/api/member/member-api";
import type { ContractAccessLog } from "src/api/member/member-types";
import Header from "src/components/layout/Header";
import MetaTag from "src/components/layout/MetaTag";
import ExcelButton from "src/pages/mypage/contract/components/ExcelButton";
import HistoryDownloadFeedbackModals, { useHistoryDownloadFeedbackModals } from "src/pages/mypage/contract/components/HistoryDownloadFeedbackModals";
import HistoryInfiniteScrollFooter from "src/pages/mypage/contract/components/HistoryInfiniteScrollFooter";
import HistoryPageControls from "src/pages/mypage/contract/components/HistoryPageControls";
import { useIFCUnsupportedRedirect } from "src/pages/mypage/contract/hooks/useIFCUnsupportedRedirect";
import useExcelDownload from "src/pages/mypage/contract/hooks/useExcelDownload";
import AccessLogList from "./components/AccessLogList";
import Spinner from "src/components/Spinner";
import {
  ACCESS_LOG_DOWNLOAD_SIZE,
  ACCESS_LOG_EXCEL_COLUMNS,
  createAccessLogExcelFileName,
  createAccessLogSearchParams,
  INITIAL_ACCESS_LOG_DATE_RANGE,
  isSelectAccessLogDateRange,
  toAccessLogTokenPage,
  transformContractAccessLogData,
} from "./functions/accessLog";
import { fetchAllFromPageToken, isDeadPageTokenError, TokenPage, unwrapPageResponse } from "./functions/accessLogPageToken";
import type { AccessLogDateRange } from "./types/accessLog";

function AccessLog() {
  const { t } = useTranslation(["access", "common"]);
  const navigate = usePartnerNavigate();
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [isLoadingNextPage, setIsLoadingNextPage] = useState(false);

  const [date, setDate] = useState<AccessLogDateRange>(INITIAL_ACCESS_LOG_DATE_RANGE);
  const [accessLogs, setAccessLogs] = useState<ContractAccessLog[]>([]);
  const { contractId } = useParams();
  // IFC 환경에서는 '이용자 출입내역'을 제공하지 않아 직접 URL 진입 시 신청/계약 상세로 리다이렉트 (PS-1744)
  const isIFC = useIFCUnsupportedRedirect(contractId);
  const [contract, setContract] = useState<ContractModel | null>(null);
  const [nextPageToken, setNextPageToken] = useState<string>();
  const [loadNextPageError, setLoadNextPageError] = useState<unknown>(null);
  const isLoadingNextPageRef = useRef(false);
  // 조회 조건이 바뀐 뒤 늦게 도착한 이전 조건의 응답을 버리기 위한 세대값
  const searchGenerationRef = useRef(0);

  const contractManageId = contract?.contractManageId;
  const contractApplyNumber = contract?.contractApplyNumber;
  const formattedAccessLogs = useMemo(() => transformContractAccessLogData(accessLogs), [accessLogs]);

  const { executeAsync: getContract, loading: contractLoading } = useApiOperation(getContractAsync);
  // 첫 페이지 실패는 보여줄 목록이 없으므로 전역 에러 모달 유지
  const { executeAsync: getFirstAccessLogPageAsync } = useApiOperation(getContractAccessLogsByPageTokenAsync);
  // 다음 페이지와 엑셀 실패는 화면에서 직접 처리
  const { executeAsync: getAccessLogPageAsync } = useApiOperation(getContractAccessLogsByPageTokenAsync, { noHandleError: true });
  const { downloadExcel } = useExcelDownload();
  const {
    modal: downloadFeedbackModal,
    openContentEmptyAlert,
    closeContentEmptyAlert,
    openDownloadAlert,
    closeDownloadAlert,
    openErrorAlert,
    closeErrorAlert,
  } = useHistoryDownloadFeedbackModals();

  const hasNextPage = Boolean(nextPageToken);
  const isReadyToSearch = Boolean(contractManageId);

  const searchFirstPage = useCallback(
    async (targetContractManageId: number, targetDate: AccessLogDateRange) => {
      const generation = ++searchGenerationRef.current;
      setSearchLoading(true);
      setLoadNextPageError(null);

      try {
        const data = unwrapPageResponse(await getFirstAccessLogPageAsync(createAccessLogSearchParams(targetContractManageId, targetDate)));
        if (generation !== searchGenerationRef.current) return;

        const page = toAccessLogTokenPage(data);
        setAccessLogs(page.items);
        setNextPageToken(page.nextPageToken);
      } catch (error) {
        console.log(error);
        // 이전 조건의 목록과 토큰이 남으면 새 조건 결과로 보이고 다음 페이지도 이전 토큰으로 요청함
        if (generation === searchGenerationRef.current) {
          setAccessLogs([]);
          setNextPageToken(undefined);
        }
      } finally {
        if (generation === searchGenerationRef.current) {
          setSearchLoading(false);
        }
      }
    },
    [getFirstAccessLogPageAsync],
  );

  const fetchContract = useCallback(
    async (targetContractId: string) => {
      const { data } = await getContract({ contractId: targetContractId });
      setContract(data?.data || null);
    },
    [getContract],
  );

  const handleOnDateRangeChange = async (nextDate: AccessLogDateRange) => {
    if (searchLoading || isLoadingNextPageRef.current) return;

    setDate(nextDate);

    if (isSelectAccessLogDateRange(nextDate) === false) {
      setAccessLogs([]);
      setNextPageToken(undefined);
      setLoadNextPageError(null);
      return;
    }
  };

  const handleOnLoadNextPage = useCallback(async () => {
    if (!contractManageId || searchLoading || isLoadingNextPageRef.current || !nextPageToken || !isSelectAccessLogDateRange(date)) return;

    const generation = searchGenerationRef.current;
    isLoadingNextPageRef.current = true;
    setIsLoadingNextPage(true);
    try {
      const data = unwrapPageResponse(await getAccessLogPageAsync(createAccessLogSearchParams(contractManageId, date, nextPageToken)));
      if (generation !== searchGenerationRef.current) return;

      const page = toAccessLogTokenPage(data);
      setAccessLogs((prevAccessLogs) => [...prevAccessLogs, ...page.items]);
      setNextPageToken(page.nextPageToken);
    } catch (error) {
      console.log(error);
      if (generation === searchGenerationRef.current) {
        setLoadNextPageError(error);
      }
    } finally {
      isLoadingNextPageRef.current = false;
      setIsLoadingNextPage(false);
    }
  }, [contractManageId, date, getAccessLogPageAsync, nextPageToken, searchLoading]);

  // 무효한 토큰은 재시도해도 계속 실패하므로 첫 페이지부터 다시 조회
  const handleRetryLoadNextPage = () => {
    if (!contractManageId || isSelectAccessLogDateRange(date) === false) return;

    if (isDeadPageTokenError(loadNextPageError)) {
      searchFirstPage(contractManageId, date);
      return;
    }

    setLoadNextPageError(null);
    handleOnLoadNextPage();
  };

  const getAccessLogExcelRows = async () => {
    if (!contractManageId || isSelectAccessLogDateRange(date) === false) {
      openErrorAlert();
      return null;
    }

    const fetchExcelPage = async (pageToken?: string): Promise<TokenPage<ContractAccessLog>> => {
      const data = unwrapPageResponse(await getAccessLogPageAsync(createAccessLogSearchParams(contractManageId, date, pageToken, ACCESS_LOG_DOWNLOAD_SIZE)));
      return toAccessLogTokenPage(data);
    };

    // 첫 조회 중에는 화면 목록이 이전 조건의 결과라 처음부터 받음
    if (searchLoading) {
      return transformContractAccessLogData(await fetchAllFromPageToken(fetchExcelPage));
    }

    try {
      const restAccessLogs = nextPageToken ? await fetchAllFromPageToken(fetchExcelPage, nextPageToken) : [];
      return transformContractAccessLogData([...accessLogs, ...restAccessLogs]);
    } catch (error) {
      if (!isDeadPageTokenError(error)) throw error;
      return transformContractAccessLogData(await fetchAllFromPageToken(fetchExcelPage));
    }
  };

  const handleDownloadExcel = async () => {
    try {
      setDownloadLoading(true);
      const rows = await getAccessLogExcelRows();

      if (!rows) return;
      if (rows.length === 0) {
        openContentEmptyAlert();
        return;
      }

      downloadExcel({
        columns: ACCESS_LOG_EXCEL_COLUMNS,
        fileName: createAccessLogExcelFileName(contractApplyNumber, date),
        rows,
        sheetName: t("excelSheetName"),
        webViewFileName: t("excelFileName"),
      });

      if (!window.ReactNativeWebView) {
        openDownloadAlert();
      }
    } catch (error) {
      console.log(error);
      openErrorAlert();
    } finally {
      setDownloadLoading(false);
    }
  };

  useEffect(() => {
    if (isIFC || !contractId) {
      setContract(null);
      return;
    }

    setContract(null);
    setAccessLogs([]);
    setNextPageToken(undefined);
    setLoadNextPageError(null);
    fetchContract(contractId);
  }, [isIFC, contractId, fetchContract]);

  useEffect(() => {
    if (!contractManageId || isSelectAccessLogDateRange(date) === false) return;

    searchFirstPage(contractManageId, date);
  }, [contractManageId, date, searchFirstPage]);

  // IFC 환경에서는 리다이렉트되므로 화면을 렌더링하지 않음
  if (isIFC) {
    return null;
  }

  return (
    <>
      <MetaTag title={`${t("title")} | TaapSpace`} />
      <Header
        title={t("title")}
        headerType="BACK"
        onClickBackButton={() => {
          navigate(-1);
        }}
      />
      <div className="access-log-container">
        <HistoryPageControls //
          title={t("title")}
          action={<ExcelButton title={t("downloadExcel")} onClick={handleDownloadExcel} disabled={downloadLoading || !isReadyToSearch} />}
          dateRange={date}
          disabled={contractLoading || searchLoading || !isReadyToSearch}
          onDateRangeChange={handleOnDateRangeChange}
        />
        <div className="access-log-list-section">
          {contractLoading || searchLoading ? (
            <div className="spinner-container">
              <Spinner size="page" />
            </div>
          ) : (
            <>
              <AccessLogList accessLogs={formattedAccessLogs} />
              {/* 0건 페이지여도 토큰이 있으면 이어받아야 하므로 목록 건수가 아니라 토큰 기준으로 노출 */}
              {(formattedAccessLogs.length > 0 || hasNextPage) && (
                <HistoryInfiniteScrollFooter //
                  hasNextPage={hasNextPage}
                  isLoadingNextPage={isLoadingNextPage}
                  disabled={searchLoading}
                  onLoadNextPage={handleOnLoadNextPage}
                  // TODO: 기획 문구 확정 전 임시값 (Ctrl.room 문구)
                  isLoadError={Boolean(loadNextPageError)}
                  errorMessage={t("loadNextPageError")}
                  retryLabel={t("retry")}
                  onRetry={handleRetryLoadNextPage}
                />
              )}
            </>
          )}
        </div>
        <HistoryDownloadFeedbackModals
          isDownloading={downloadLoading}
          modal={downloadFeedbackModal}
          emptyMessage={
            <>
              <p>{t("emptyPeriod")}</p>
              <p>{t("emptyContent")}</p>
            </>
          }
          onCloseContentEmptyAlert={closeContentEmptyAlert}
          onCloseDownloadAlert={closeDownloadAlert}
          onCloseErrorAlert={closeErrorAlert}
        />
      </div>
    </>
  );
}
export default AccessLog;
